import { DRIVER_COLORS } from '../hooks/useRouteMonitoringJoin';
import { CARDS_PER_SHEET } from '../packaging/PackagingSheet';
import type { PackagingCustomerEntry, PackagingResult } from '../packaging/packagingData';

// The row count here can differ from the board's "awaiting departure" count:
// `/api/packaging` skips jobs whose order is missing or has no items that day
// (`delivery/app/packaging.ts`), and a job whose driver name cannot be
// resolved lands in the `unassigned` bucket. Rows count what would be printed.

/** A driver with one of these has already left, so the board no longer shows them here. */
const STARTED_STATUSES = ['in_progress', 'completed', 'exception'] as const;

export interface DepartureRow {
  key: string;
  name: string;
  /** By display position, like the route page: no two rows on a screen share a color. */
  color: string;
  entries: PackagingCustomerEntry[];
  jobIds: string[];
  pages: number;
}

/**
 * The board's "awaiting departure" rule (`fulfillment-jobs-queries.ts`: dispatched,
 * has a driver, and that driver has no started job that day) applied to the
 * date's packaging groups. Order is the backend's.
 */
export function deriveDepartureRows(result: PackagingResult): DepartureRow[] {
  const rows: DepartureRow[] = [];
  for (const group of result.groups) {
    if (group.key === 'unassigned') continue;
    const started = group.customers.some((c) =>
      (STARTED_STATUSES as readonly string[]).includes(c.job_status),
    );
    if (started) continue;
    const entries = group.customers.filter((c) => c.job_status === 'dispatched');
    if (entries.length === 0) continue;
    rows.push({
      key: group.key,
      name: group.label ?? group.key,
      color: DRIVER_COLORS[rows.length % DRIVER_COLORS.length]!,
      entries,
      jobIds: entries.map((c) => c.job_id),
      pages: Math.ceil(entries.length / CARDS_PER_SHEET),
    });
  }
  return rows;
}
